/**
 * 1.81.0 · Servidor local: criptografía del vínculo entre el servidor y los puestos (WebCrypto).
 *
 * - Vincular: cada lado genera una clave ECDH P-256 y se intercambian la parte pública. Con el secreto
 *   común se calcula un código de 6 dígitos que se ve en las dos pantallas (para «Aceptar» en el
 *   servidor) y, si se vincula con el código del servidor, el puesto prueba que lo conoce (HMAC).
 * - Al vincular, el servidor le da al puesto una clave propia (32 bytes al azar). En cada conexión se
 *   deriva una clave de sesión (HKDF con la clave del puesto, un ECDH nuevo y dos números al azar) y todo viaja con AES-GCM.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();
const subtle = () => globalThis.crypto.subtle;

export function b64(bytes: ArrayBuffer | Uint8Array): string {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(s);
}
export function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
export const randomBytes = (n: number) => globalThis.crypto.getRandomValues(new Uint8Array(n));
export const randomB64 = (n: number) => b64(randomBytes(n));

/** Copia a un ArrayBuffer propio (WebCrypto no acepta vistas de buffers compartidos en algunos motores). */
const buf = (u: Uint8Array): ArrayBuffer => u.slice().buffer as ArrayBuffer;

export interface EcdhPair { privateKey: CryptoKey; publicB64: string }

export async function ecdhPair(): Promise<EcdhPair> {
  const k = (await subtle().generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"])) as CryptoKeyPair;
  const raw = await subtle().exportKey("raw", k.publicKey);
  return { privateKey: k.privateKey, publicB64: b64(raw) };
}

/** Secreto común (32 bytes) con la parte pública del otro lado. */
export async function ecdhSecret(mine: CryptoKey, otherPublicB64: string): Promise<Uint8Array> {
  const pub = await subtle().importKey("raw", buf(unb64(otherPublicB64)), { name: "ECDH", namedCurve: "P-256" }, false, []);
  return new Uint8Array(await subtle().deriveBits({ name: "ECDH", public: pub }, mine, 256));
}

export async function hkdf(secret: Uint8Array, salt: Uint8Array, info: string, bytes = 32): Promise<Uint8Array> {
  const k = await subtle().importKey("raw", buf(secret), "HKDF", false, ["deriveBits"]);
  return new Uint8Array(await subtle().deriveBits({ name: "HKDF", hash: "SHA-256", salt: buf(salt), info: enc.encode(info) }, k, bytes * 8));
}

export async function hmac(key: Uint8Array, text: string): Promise<string> {
  const k = await subtle().importKey("raw", buf(key), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return b64(await subtle().sign("HMAC", k, enc.encode(text)));
}

/** Comparación sin cortar en el primer carácter distinto. */
export function sameText(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return d === 0;
}

/** Orden fijo de las dos claves públicas (las dos puntas calculan lo mismo). */
const transcript = (pubA: string, pubB: string) => (pubA < pubB ? `${pubA}|${pubB}` : `${pubB}|${pubA}`);

/** Código de 6 dígitos del vínculo: el mismo en las dos pantallas si nadie se metió en el medio. */
export async function pairingSas(secret: Uint8Array, pubA: string, pubB: string): Promise<string> {
  const k = await hkdf(secret, enc.encode(transcript(pubA, pubB)), "sofia-lan-sas", 4);
  const n = ((k[0]! << 24) | (k[1]! << 16) | (k[2]! << 8) | k[3]!) >>> 0;
  return String(n % 1_000_000).padStart(6, "0");
}

/** Prueba de que el puesto conoce el código que muestra el servidor (sin mandarlo). */
export async function codeProof(secret: Uint8Array, pubA: string, pubB: string, code: string): Promise<string> {
  const k = await hkdf(secret, enc.encode(transcript(pubA, pubB)), "sofia-lan-code");
  return hmac(k, `code:${code.replace(/\D/g, "")}`);
}

/** Clave para el mensaje del vínculo (la clave propia del puesto viaja cifrada con esta). */
export async function pairingKey(secret: Uint8Array, pubA: string, pubB: string): Promise<Uint8Array> {
  return hkdf(secret, enc.encode(transcript(pubA, pubB)), "sofia-lan-pair");
}

/**
 * 1.99.5 · Clave de la sesión con un ECDH nuevo por conexión: la clave del puesto (prueba que es el puesto
 * vinculado) + el secreto de las dos claves de esta conexión (si algún día se filtra la clave del puesto, lo
 * grabado antes no se puede descifrar) + un número al azar de cada lado.
 */
export async function sessionKey2(deviceKeyB64: string, ephSecret: Uint8Array, nonceClient: string, nonceServer: string, pkClient: string, pkServer: string, transcript = ""): Promise<Uint8Array> {
  const dk = unb64(deviceKeyB64);
  const ikm = new Uint8Array(dk.length + ephSecret.length);
  ikm.set(dk, 0); ikm.set(ephSecret, dk.length);
  // `transcript`: lo que cada lado dijo sin cifrar en el saludo (versión, protocolo, base…). Si alguien lo cambió
  // en el camino, las claves no coinciden y la conexión no arranca.
  return hkdf(ikm, enc.encode(`${nonceClient}|${nonceServer}|${pkClient}|${pkServer}|${transcript}`), "sofia-lan-session-2");
}

/**
 * 1.99.5 · Lo que publica la búsqueda en la red en vez del negocio y la sucursal: una huella (el puesto la
 * calcula con el id de su negocio y la compara; quien no lo conoce no sabe de qué negocio es).
 */
export async function lanTag(kind: "biz" | "loc", id: string): Promise<string> {
  const d = new Uint8Array(await subtle().digest("SHA-256", enc.encode(`sofia-lan:${kind}:${id}`)));
  return b64(d.slice(0, 12)).replace(/\+/g, "-").replace(/\//g, "_");
}

export async function aesKey(raw: Uint8Array): Promise<CryptoKey> {
  return subtle().importKey("raw", buf(raw), { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export interface Sealed { iv: string; c: string }

export async function seal(key: CryptoKey, text: string, aad: string): Promise<Sealed> {
  const iv = randomBytes(12);
  const c = await subtle().encrypt({ name: "AES-GCM", iv: buf(iv), additionalData: enc.encode(aad) }, key, enc.encode(text));
  return { iv: b64(iv), c: b64(c) };
}

/** Descifra; si el mensaje fue cambiado o es de otra clave, falla. */
export async function open(key: CryptoKey, s: Sealed, aad: string): Promise<string> {
  const p = await subtle().decrypt({ name: "AES-GCM", iv: buf(unb64(s.iv)), additionalData: enc.encode(aad) }, key, buf(unb64(s.c)));
  return dec.decode(p);
}

/** Huella corta de una clave (para mostrarla o compararla sin guardarla entera). */
export async function fingerprint(b64Key: string): Promise<string> {
  const d = new Uint8Array(await subtle().digest("SHA-256", buf(unb64(b64Key))));
  return Array.from(d.slice(0, 6), (x) => x.toString(16).padStart(2, "0")).join(":");
}
